import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { useNavigate } from 'react-router'
import { positionDropdown } from '../../../../../common/dropdown'
import { UseI18n } from '../../../../../common/hooks/useI18n'
import { Style } from '../../../../../common/html/Style'
import { Element } from '../../../../../common/html/element'
import { Icon } from '../../../../../common/html/icon'
import { InputDropdown } from '../../../../../common/html/input/InputDropdown'
import { Loading } from '../../../../../common/loading'
import { ScrollComponent } from '../../../../../common/scrollComponent'
import { Tooltip } from '../../../../../common/tooltip'
import { selectCompanyInfo } from '../../../../../companyAnalysis/common/header/companyInfo/store/slice'
import { debounce } from '../../../../../helpers/common'
import { highlightString } from '../../../../../layout/header/search/helpers'
import { COMPONENTS } from '../../../../../routers/constants'
import { Z_INDEX } from '../../../../../utils/common'
import { IGNORE_QUERY } from '../../../constant'
import classes from '../index.module.scss'
import {
  KEY_LOADING,
  selectCompanies,
  selectCompanyType,
  selectLoading,
} from '../store/slice'
import { searchCompanyThunk } from '../store/thunk'

const CLASS_NAME_LINK = 'linkToSummary'

const PAGE_SIZE = 50

const InputCompany = () => {
  const dispatch = useDispatch()

  const navigate = useNavigate()

  const loading = useSelector(selectLoading(KEY_LOADING.COMPANY))
  const companies = useSelector(selectCompanies)
  const companyType = useSelector(selectCompanyType)
  const companyInfo = useSelector(selectCompanyInfo)

  const tooltipRef = useRef()

  const plhSearch = UseI18n('dynamicReport.SEARCH_COMPANY')

  const [isShowDropdown, setIsShowDropdown] = useState(false)
  const [value, setValue] = useState('')

  useEffect(() => {
    setValue(companyInfo.organizationName || '')
  }, [companyInfo])

  const onChange = (e) => {
    const val = e.target.value
    setValue(val)
    callApiSearch(val.trim())
  }

  const callApiSearch = useCallback(
    debounce((value) => {
      dispatch(
        searchCompanyThunk({ term: value, companyType, pageSize: PAGE_SIZE }),
      )
    }, 500),
    [companyType],
  )

  const openDropdown = () => {
    if (!isShowDropdown) {
      dispatch(
        searchCompanyThunk({
          term: value.trim(),
          companyType,
          pageSize: PAGE_SIZE,
        }),
      )
      setIsShowDropdown(true)
    }
  }

  const clickOutside = (el) => {
    if (companyInfo) {
      setValue(companyInfo.organizationName || '')
    }
    if (el.className !== CLASS_NAME_LINK) {
      setIsShowDropdown(false)
    }
  }

  const chooseCompany = (company) => () => {
    if (
      company.allowDynamicReport &&
      company.organizationId !== companyInfo.organizationId
    ) {
      navigate(
        COMPONENTS.dynamicReport.route +
          '?organizationId=' +
          company.organizationId +
          '&' +
          IGNORE_QUERY,
        {
          replace: true,
        },
      )
    }
    setIsShowDropdown(false)
  }

  const goToCompanyAnalysis = (organizationId) => () => {
    window.open(
      COMPONENTS.companyAnalysis.components.summary.route +
        '?organizationId=' +
        organizationId,
    )
    if (companyInfo) {
      setValue(companyInfo.organizationName || '')
    }
    setIsShowDropdown(false)
  }

  const getContentCompany = (isBoughtReport, organizationId, children) => {
    if (!isBoughtReport) {
      return (
        <Tooltip
          zIndex={Z_INDEX.MODAL + 1}
          position={positionDropdown.TOP_START_RIGHT}
          customPosition={(position) => ({
            ...position,
            top: position.top - 10,
            left: position.left + 292,
          })}
          eventType="click"
          isDelayMouseOut
          isInsideContent
          tooltipContent={
            <div ref={tooltipRef}>
              <Translate value="dynamicReport.NOTE_BUY_FACTSHEET_1" />
              <Element
                className="cursorPointer"
                elementName="span"
                style={{ color: '#2C73E8' }}
                onClick={goToCompanyAnalysis(organizationId)}
              >
                <Translate
                  className={CLASS_NAME_LINK}
                  value="dynamicReport.NOTE_BUY_FACTSHEET_2"
                />
              </Element>
              <Translate value="dynamicReport.NOTE_BUY_FACTSHEET_3" />
            </div>
          }
        >
          {children}
        </Tooltip>
      )
    }
    return children
  }

  return (
    <InputDropdown
      isShowDropdown={isShowDropdown}
      placeHolder={plhSearch}
      onClick={openDropdown}
      onClickOutsideDropdown={clickOutside}
      onChange={onChange}
      value={value}
      style={{ fontSize: 12, color: '#3A414B' }}
      dropdownContentStyle={{
        minWidth: '330px',
        boxShadow: '1px 10px 12px 4px rgba(0, 0, 0, 0.2)',
      }}
      exceptRef={[tooltipRef]}
    >
      <div className={classes.companyContainer}>
        <ScrollComponent autoHeight autoHeightMax={450}>
          <div>
            {loading ? (
              <div className={classes.loadingCompany}>
                <Loading />
              </div>
            ) : (
              <div>
                {companies.map((company, index) => {
                  return (
                    <div key={company.organizationId}>
                      {getContentCompany(
                        company.allowDynamicReport,
                        company.organizationId,
                        <Element
                          className={classes.itemCompany}
                          style={{
                            color: company.allowDynamicReport
                              ? '#191D25'
                              : '#9C9C9C',
                            borderBottom: '1px solid #E8EAED',
                            borderTop: !index ? '1px solid #E8EAED' : undefined,
                            cursor: company.allowDynamicReport
                              ? 'pointer'
                              : undefined,
                          }}
                          onClick={chooseCompany(company)}
                        >
                          <div className="w100">
                            {company.taxCode && (
                              <>
                                {highlightString(company.taxCode, value.trim())}
                                {' - '}
                              </>
                            )}
                            {company.ticker && (
                              <>
                                {highlightString(company.ticker, value.trim())}
                                {' - '}
                              </>
                            )}
                            {highlightString(
                              company.organizationName,
                              value.trim(),
                            )}
                          </div>
                          {!company.allowDynamicReport && (
                            <Icon
                              style={{ color: '#3A414B' }}
                              className="icon-lock pl1"
                            />
                          )}
                        </Element>,
                      )}
                    </div>
                  )
                })}
                <Style
                  className={classes.itemCompany}
                  pseudo="hover"
                  attr="background-color"
                  color="#F3F3F3"
                />
              </div>
            )}
          </div>
        </ScrollComponent>
      </div>
    </InputDropdown>
  )
}

export default InputCompany
